<template>
  <global-modal name="event-modal-catalog" @before-open="beforeOpen">
    <Modal
      @close="closeModal"
      :confirm-close="status === 'draft'"
      :confirm-close-text="$t('event.catalog.confirmClose')"
      ref="emc"
    >
      <div class="event-modal-catalog">
        <EventModalCatalogHeader />
        <EventModalCatalogNavigation
          v-if="status === 'draft'"
          @nextStep="nextStep"
        />

        <div v-if="isCatalogDataLoading" class="event-modal-catalog__loader">
          <Loader is-absolute white />
        </div>

        <div v-else class="event-modal-catalog__container">
          <EventModalCatalogParticipantsListForm
            ref="catalogParticipantsListForm"
            v-if="step === steps.ALPHABET_LIST"
          />

          <EventModalCatalogRubricsForm v-if="step === steps.RUBRICS" />

          <EventModalCatalogRussianExporter
            v-if="step === steps.RUSSIAN_EXPORTER"
          />

          <EventModalCatalogPackageForm
            v-if="step === steps.PACKAGE_PLACEMENT"
          />

          <EventModalCatalogAdServicesForm v-if="step === steps.AD_SERVICE" />

          <EventModalCatalogPreview
            v-if="step === steps.PREVIEW"
            :is-download-order="isDownloadOrder"
          />
        </div>

        <EventModalCatalogFooter
          v-if="step <= steps.PREVIEW && step >= steps.ALPHABET_LIST"
          @next="nextStep"
          @scroll-to-top="scrollToTop"
          @closeModal="closeModal"
        >
          <template
            v-if="step === steps.RUBRICS && rubricsWithServices"
            slot="paid-services"
          >
            <div v-if="isNextStepAllowed">
              <template v-if="selectedRubricsId && selectedRubricsId.length">
                {{ $t("event.catalog.modal.rubricator.chosenNote") }}
                <span class="primary">
                  {{ selectedRubricsId.length }}
                  {{
                    getDeclinationWord(
                      selectedRubricsId.length,
                      this.$t("event.catalog.modal.rubricator.countRubrics")
                    )
                  }}.
                </span>
              </template>

              <span
                v-if="strAllCountPaidServicesRubrics || paidServicesRubrics"
              >
                {{ $t("event.catalog.modal.rubricator.footerPaidServices") }}:
              </span>
              <span class="primary" v-html="paidServicesRubrics"></span>
            </div>
            <div v-else>
              {{
                $t("event.catalog.modal.rubricator.footerLastStepPleaseSelect")
              }}
            </div>
          </template>
        </EventModalCatalogFooter>
      </div>
    </Modal>
  </global-modal>
</template>

<script>
import Modal from "@/components/Modal/Modal.vue";
import apiCatalog from "@/api/catalog";

import EventModalCatalogHeader from "./EventModalCatalogHeader.vue";
import EventModalCatalogNavigation from "./EventModalCatalogNavigation.vue";
import EventModalCatalogFooter from "./EventModalCatalogFooter.vue";

import EventModalCatalogParticipantsListForm from "./EventModalCatalogParticipantsListForm.vue";
import EventModalCatalogRubricsForm from "./EventModalCatalogRubricsForm.vue";
import EventModalCatalogRussianExporter from "./EventModalCatalogRussianExporter.vue";
import EventModalCatalogPackageForm from "./EventModalCatalogPackageForm.vue";
import EventModalCatalogAdServicesForm from "./EventModalCatalogAdServicesForm.vue";
import EventModalCatalogPreview from "./EventModalCatalogPreview.vue";

import { mapState, mapActions, mapMutations, mapGetters } from "vuex";
import store from "@/store";
import getError from "@/helpers/error";
import { Loader } from "@v2/shared/ui/components";

export default {
  name: "EventModalCatalog",

  components: {
    Loader,
    Modal,

    EventModalCatalogHeader,
    EventModalCatalogNavigation,
    EventModalCatalogFooter,

    EventModalCatalogParticipantsListForm,
    EventModalCatalogRubricsForm,
    EventModalCatalogRussianExporter,
    EventModalCatalogPackageForm,
    EventModalCatalogAdServicesForm,

    EventModalCatalogPreview
  },

  data() {
    return {
      isDownloadOrder: false
    };
  },

  computed: {
    ...mapState("event", ["event"]),
    ...mapGetters("event", ["eventId"]),

    ...mapState("eventCatalog", [
      "info",
      "currenciesComparisonTable",
      "status",
      "step",
      "steps",
      "selectedServicesForRubrics",
      "selectedRubricsId",
      "alphabetList",
      "isCatalogDataLoading"
    ]),

    ...mapGetters("eventCatalog", [
      "isNextStepAllowed",
      "rubricsWithServices",
      "getOrderDataFromCatalog"
    ]),

    hasOrder() {
      return this.status === "confirmed";
    },

    selectedRubricsPackages() {
      return JSON.parse(
        JSON.stringify(store.getters["eventCatalog/selectedRubricsPackages"])
      );
    },

    paidServicesRubrics() {
      let strPaidServicesRubrics = "";
      let listServices = [];

      this.rubricsWithServices.forEach(rubric => {
        if (rubric.services && rubric.services.length) {
          rubric.services.forEach(service => {
            const isSelected =
              Array.isArray(this.selectedServicesForRubrics[rubric.id]) &&
              this.selectedServicesForRubrics[rubric.id].indexOf(service.id) >
                -1;

            if (service.price !== 0 && isSelected) {
              listServices.push(service.title);
            }
          });
        }
      });

      const assListServices = listServices.reduce((acc, rec) => {
        return typeof acc[rec] !== "undefined"
          ? { ...acc, [rec]: acc[rec] + 1 }
          : { ...acc, [rec]: 1 };
      }, {});

      for (let el in assListServices) {
        if (el && Object.prototype.hasOwnProperty.call(assListServices, el)) {
          strPaidServicesRubrics += strPaidServicesRubrics.length
            ? `, ${el.trim()}&nbsp;${assListServices[el]}`
            : `${el.trim()}&nbsp;${assListServices[el]}`;
        }
      }

      this.selectedRubricsPackages.forEach(el => {
        if (el && el.title) {
          strPaidServicesRubrics += strPaidServicesRubrics.length
            ? `, ${el.title}&nbsp;${el.count}`
            : `${el.title}&nbsp;${el.count}`;
        }
      });

      return strPaidServicesRubrics;
    },

    strAllCountPaidServicesRubrics() {
      if (this.rubricsWithServices && this.rubricsWithServices.length) {
        // Рубрика считается платной, если у неё есть цена,
        // либо если нет цены, но при этом у одна из услуг вошла в пакет (имеет oldPrice)
        const paidRubrics = this.rubricsWithServices.filter(el => {
          let isPaidService = false;

          if (el && el.services && el.services.length) {
            el.services.forEach(service => {
              if (service.oldPrice && service.oldPrice !== 0) {
                isPaidService = true;
              }
            });
          }

          return el.totalPrice !== 0 || isPaidService;
        });

        if (paidRubrics && paidRubrics.length) {
          const title = this.getDeclinationWord(
            paidRubrics.length,
            this.$t("event.catalog.modal.rubricator.countPaidRubrics")
          );
          return `${paidRubrics.length}&nbsp;${title}, `;
        }
      }

      return "";
    }
  },

  methods: {
    ...mapActions("eventCatalog", [
      "fetchInfo",
      "fetchData",
      "fetchRubrics",
      "setCurrentCurrencyType",
      "resetCatalogOrderState",
      "loadingCatalogData"
    ]),
    ...mapMutations("eventCatalog", ["UPDATE_STEP"]),
    closeModal() {
      this.$modal.hide("event-modal-catalog");
      this.$store.commit("eventCatalog/IS_CATALOG_DATA_LOADING", true);
    },
    async beforeOpen() {
      this.resetCatalogOrderState();
      this.$store.commit("eventCatalog/IS_CATALOG_DATA_LOADING", true);
      await this.fetchInfo({
        eventId: this.event.id,
        templateId: this.event.catalog_template_id
      });

      if (this.event.catalog_order_id) {
        await this.fetchData({
          eventId: this.event.id,
          orderId: this.event.catalog_order_id
        })
          .then(order => {
            if (Object.values(this.steps).indexOf(order.step) > -1) {
              if (order.status !== "draft") {
                this.UPDATE_STEP(this.steps.PREVIEW);
              } else {
                this.UPDATE_STEP(order.step);
              }
            }
          })
          .catch(() => {
            this.$modal.hide("event-modal-catalog");
          });
      } else {
        if (this.info.currencies && this.info.currencies.length) {
          this.setCurrentCurrencyType(
            this.currenciesComparisonTable.find(
              el => el.code === this.info.currencies[0]
            )
          );
        }
      }

      await this.fetchRubrics(this.event.id);

      this.$store.commit("eventCatalog/IS_CATALOG_DATA_LOADING", false);
    },

    scrollToTop() {
      this.$refs.emc.scrollToTop();
    },

    getDeclinationWord(count, text_forms) {
      if (count) {
        const n = Math.abs(parseInt(count)) % 100;
        var n1 = n % 10;

        if (n > 10 && n < 20) {
          return text_forms[2];
        }
        if (n1 > 1 && n1 < 5) {
          return text_forms[1];
        }
        if (n1 === 1) {
          return text_forms[0];
        }

        return text_forms[2];
      }

      return null;
    },

    async nextStep(selectedStep = null) {
      this.loadingCatalogData(true);

      try {
        if (this.step === this.steps.ALPHABET_LIST) {
          await this.handleAlphabetListStep();
        } else {
          await apiCatalog.update(
            this.eventId,
            this.getOrderDataFromCatalog,
            this.event.catalog_order_id
          );
        }

        const nextStep = selectedStep || this.calculateNextStep();
        this.UPDATE_STEP(nextStep);
        this.scrollToTop();
      } catch (error) {
        this.$store.dispatch("common/setErrorMessage", getError(error));
      } finally {
        this.loadingCatalogData(false);
      }
    },

    async handleAlphabetListStep() {
      if (this.$refs.catalogParticipantsListForm?.nextStep) {
        await this.$refs.catalogParticipantsListForm.nextStep();
      }
    },

    calculateNextStep() {
      if (
        !this.alphabetList.companyRussianExporter &&
        this.step === this.steps.RUBRICS
      ) {
        return this.step + 2;
      }

      return this.step + 1;
    }
  },
  watch: {
    hasOrder(value) {
      if (["draft"].includes(value)) {
        this.UPDATE_STEP(this.steps.PREVIEW);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/media";

.event-modal-catalog {
  background: rgba(#f9f9f9, 0.5);
  min-height: calc(100 * var(--vh));
  display: flex;
  flex-direction: column;

  &__loader {
    position: relative;
    min-height: 30vh;
  }

  &__container {
    padding: 40px 0 40px 80px;

    width: 780px;

    margin-bottom: auto;

    @include respond-down(lg) {
      padding: 40px 24px 24px 24px;
      width: 100%;
      box-sizing: border-box;
    }

    @include respond-down(md) {
      padding: 20px 16px 16px 16px;
    }
  }
}
</style>
